import { ReachabilityRef } from '../../core/models/reachability-ref.model';
import { NumberTag } from '../cc-numbers/cc-numbers.model';
export type { ReachabilityRef };

/** Call notification (PS-scoped) or AM notification (UA-scoped) */
export interface Notification {
  notificationId: number;
  urgentOnly: boolean;
  reachability: ReachabilityRef;
}

/** Forwarding (manual, busy, direct) */
export interface Forwarding {
  forwardingId: number;
  reachability: ReachabilityRef;
  order: number;
  // Schedule fields (busy + direct only)
  dayOfWeek: number | null;
  startTime: string | null;
  endTime: string | null;
  startDate: string | null;
  endDate: string | null;
}

/** Addable reachability (same shape as ReachabilityRef) */
export type AddableReachability = ReachabilityRef;

/** Direct forwarding group: one number and its forwardings */
export interface DirectForwardingGroup {
  number: string;
  type: string;
  /** Tags of the number (from PS-API `/numbers`) — customer-visible labels. */
  tags: NumberTag[];
  forwardings: Forwarding[];
}

/** A number that can carry direct forwardings (input for building a {@link DirectForwardingGroup}). */
export type DirectForwardingNumber = Pick<DirectForwardingGroup, 'number' | 'type' | 'tags'>;

/** Day of week options matching old CMT (null=all, 0=Sunday..6=Saturday) */
export const DAY_OF_WEEK_OPTIONS: { value: number | null; label: string }[] = [
  { value: null, label: 'All days' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
];

/** Helper: format day-of-week value for display */
export function formatDayOfWeek(day: number | null): string {
  const opt = DAY_OF_WEEK_OPTIONS.find(d => d.value === day);
  return opt?.label ?? 'All days';
}

/** Helper: format schedule summary for a forwarding */
export function formatSchedule(f: Forwarding): string {
  const parts: string[] = [];
  if (f.dayOfWeek !== null) {
    parts.push(formatDayOfWeek(f.dayOfWeek));
  }
  if (f.startTime && f.endTime) {
    parts.push(`${f.startTime} - ${f.endTime}`);
  }
  if (f.startDate && f.endDate) {
    parts.push(`${f.startDate} to ${f.endDate}`);
  } else if (f.startDate) {
    parts.push(`from ${f.startDate}`);
  } else if (f.endDate) {
    parts.push(`until ${f.endDate}`);
  }
  return parts.length > 0 ? parts.join(' | ') : '—';
}
